'use client'

import { useTranslations } from 'next-intl'
import { Menu, Search, LogOut, Loader2, FolderKanban, CheckSquare, UserCircle, Clock, AlertTriangle, User, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ThemeToggle } from './theme-toggle'
import { LanguageSelector } from './language-selector'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/stores/auth-store'
import { useTimeTracking } from '@/lib/stores/time-tracking-store'
import Link from 'next/link'
import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { getAuthHeaders } from '@/lib/api-utils'
import { HelpSidebarContent } from './help-sidebar-content'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface HeaderProps {
  onToggleSidebar: () => void
  onToggleSidebarMobile?: () => void
  sidebarCollapsed: boolean
}

interface SearchResult {
  id: number
  type: 'project' | 'issue' | 'user'
  title: string
  subtitle?: string
  url: string
  metadata?: {
    status?: string
    tracker?: string
    project?: string
    assignee?: string
  }
}

export function Header({ onToggleSidebar, onToggleSidebarMobile }: HeaderProps) {
  const t = useTranslations()
  const { user, logout, session } = useAuth()
  const { activeTimer, getFormattedTime } = useTimeTracking()

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [showLogoutAlert, setShowLogoutAlert] = useState(false)
  const [showHelpSidebar, setShowHelpSidebar] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  // Check for session expiry on mount and periodically
  useEffect(() => {
    const checkSessionExpiry = () => {
      if (session?.expiresAt && Date.now() >= session.expiresAt) {
        console.log('[Header] Session expired, logging out')
        const { handleAuthError } = require('@/lib/api-error-handler')
        handleAuthError({ message: 'Session expired' })
      }
    }

    // Check immediately
    checkSessionExpiry()

    // Check every 30 seconds
    const interval = setInterval(checkSessionExpiry, 30000)

    return () => clearInterval(interval)
  }, [session?.expiresAt])

  // Close search results when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Real-time search
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchQuery.trim().length < 1) {
        setSearchResults([])
        setShowSearchResults(false)
        return
      }

      setIsSearching(true)
      setShowSearchResults(true)

      try {
        const authHeaders = getAuthHeaders()

        // Use unified search API
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(searchQuery)}&limit=15`,
          { headers: authHeaders }
        )

        if (response.ok) {
          const data = await response.json()
          setSearchResults(data.results || [])
        } else {
          setSearchResults([])
        }
      } catch (error) {
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300) // Debounce search

    return () => clearTimeout(searchTimeout)
  }, [searchQuery])

  const handleLogout = () => {
    // Check if there's an active timer
    if (activeTimer) {
      setShowLogoutAlert(true)
      return
    }

    logout()
  }

  const handleSearchResultClick = (result: SearchResult) => {
    router.push(result.url)
    setShowSearchResults(false)
    setSearchQuery('')
  }

  return (
    <>
      <AlertDialog open={showLogoutAlert} onOpenChange={setShowLogoutAlert}>
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/20">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <AlertDialogTitle className="text-xl">{t('header.timerRunning')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base space-y-4 pt-4">
              <div className="bg-muted p-4 rounded-lg space-y-3">
                <div className="flex items-start gap-2">
                  <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      #{activeTimer?.issueId}: {activeTimer?.issueSubject}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Activity: {activeTimer?.activityName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-sm text-muted-foreground">{t('header.elapsedTime')}:</span>
                  <span className="text-lg font-mono font-bold text-primary">
                    {getFormattedTime()}
                  </span>
                </div>
              </div>

              <p className="text-foreground">
                {t('header.pleaseLogTime')}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <header className="fixed top-0 left-0 right-0 h-16 bg-background border-b border-border z-50">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              // Prefer mobile toggle on small viewports
              if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
                onToggleSidebarMobile?.()
              } else {
                onToggleSidebar()
              }
            }}
            className="md:inline-flex"
            aria-label="Toggle navigation"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex items-center">
            {/* Logo with dynamic coloring based on theme */}
            <svg 
              viewBox="450 320 1050 450" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-auto"
            >
              <style>{`
                .logo-ox {
                  fill: hsl(var(--primary));
                }
                .dark .logo-ox {
                  fill: white;
                }
                .logo-text {
                  fill: hsl(var(--foreground));
                }
                .dark .logo-text {
                  fill: white;
                }
              `}</style>
              {/* OX symbol with circle - gets theme color */}
              <g className="logo-ox">
                <g>
                  <path 
                    d="M589.1,479.1c-33.6,0-60.9,27.3-60.9,60.9s27.3,60.9,60.9,60.9,60.9-27.3,60.9-60.9-27.3-60.9-60.9-60.9ZM589.1,567.7c-15.3,0-27.7-12.4-27.7-27.7s12.4-27.7,27.7-27.7,27.7,12.4,27.7,27.7-12.4,27.7-27.7,27.7Z"
                  />
                  <polygon 
                    points="770.4 504.6 745.6 479.8 710.2 515.2 674.8 479.8 650 504.6 685.4 540 650 575.4 674.8 600.2 710.2 564.8 745.6 600.2 770.4 575.4 735 540 770.4 504.6"
                  />
                </g>
                <path 
                  d="M649.7,396c48.1,0,91.8,13.2,111.3,33.6l.2.2.2.2c19.8,20,32.5,63.8,32.1,111.8-.3,46.9-13.2,89.7-32.8,108.8l-.2.2-.2.2c-19.6,20-63.1,32.9-110.7,32.9s-91.1-12.9-110.7-32.9l-.2-.2-.2-.2c-20-19.6-32.9-63.1-32.9-110.8,0-47.6,12.9-91.1,32.9-110.7l.2-.2.2-.2c19.7-20,63.2-33,110.8-33M649.7,365c-52.6,0-105.2,14.1-132.9,42.2-56.2,55.2-56.2,210.4,0,265.7,27.6,28.1,80.2,42.2,132.8,42.2s105.2-14.1,132.8-42.2c56-54.8,56-209.3,1-264.6-27.6-28.9-80.7-43.2-133.7-43.2h0Z"
                />
              </g>
              {/* "Oxigen" text - gets foreground color (black/white) */}
              <g className="logo-text">
                <path 
                  d="M851.6,539.9c0-32.8,24-56.8,58.1-56.8s58.1,24,58.1,56.8-24,56.8-58.1,56.8-58.1-24-58.1-56.8ZM941.4,539.9c0-19-12.7-32.6-31.7-32.6s-31.9,13.6-31.9,32.6,12.9,32.6,31.9,32.6,31.7-13.6,31.7-32.6Z"
                />
                <path 
                  d="M1000.4,538.8l-38-52.9h31.5l26,38.9,26-38.9h30.8l-38.5,52.9,40.2,55.1h-31.5l-28-41.5-27.8,41.5h-30.6l39.8-55.1Z"
                />
                <path 
                  d="M1081.5,454.7c0-10.1,7.2-17.5,17.3-17.5s17.3,7.4,17.3,17.5-7.2,17.3-17.3,17.3-17.3-7.2-17.3-17.3ZM1085.6,485.9h26v108h-26v-108Z"
                />
                <path 
                  d="M1125.8,605.7h26.2c4.4,10.1,14.9,14.6,28.6,14.6s31.3-9,31.3-34.1v-9.4c-7.4,12-20.3,18.6-36.5,18.6-25.8,0-53.3-17.3-53.3-55.5s24-56.8,58.1-56.8,57.7,22.7,57.7,57.7v44.6c0,41.3-25.6,57.5-57.3,57.5s-48.1-11.6-54.9-37.2ZM1212,539.9c0-19-12.7-32.6-31.7-32.6s-31.9,13.6-31.9,32.6,12.9,31.3,31.9,31.3,31.7-12.2,31.7-31.3Z"
                />
                <path 
                  d="M1244.7,539.9c0-32.8,24-56.8,58.1-56.8s57.7,24,57.7,56.8v9.2h-88.5c3.5,15.7,15.5,24,30.8,24s20.1-3.3,25.6-10.3h27.8c-8.1,20.5-27.8,33.9-53.3,33.9s-58.1-24-58.1-56.8ZM1333,528.3c-4.2-14.4-16-21.6-30.2-21.6s-26,7.4-30.2,21.6h60.3Z"
                />
                <path 
                  d="M1367.3,537.7c0-36.1,21.4-54.6,53.3-54.6s53.3,18.4,53.3,54.6v56.2h-26v-56.2c0-20.5-10.9-30.4-27.3-30.4s-27.3,9.8-27.3,30.4v56.2h-26v-56.2Z"
                />
              </g>
            </svg>
          </div>
        </div>

  {/* Search (visible on md+ only) */}
  <div className="hidden md:block flex-1 max-w-md mx-4" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('header.searchPlaceholder')}
              className={cn('pl-8', mobileSearchOpen ? 'w-full' : '')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => {
                setMobileSearchOpen(true)
                searchQuery.length >= 1 && setShowSearchResults(true)
              }}
            />
            {isSearching && (
              <Loader2 className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground animate-spin" />
            )}

            {/* Search Results Dropdown */}
            {showSearchResults && searchQuery.length >= 1 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg max-h-96 overflow-y-auto z-50">
                {searchResults.length > 0 ? (
                  <div className="py-2">
                    {searchResults.map((result) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleSearchResultClick(result)}
                        className="w-full text-left px-3 py-2 transition-colors flex items-start gap-3"
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {result.type === 'project' ? (
                            <FolderKanban className="h-4 w-4 text-primary" />
                          ) : result.type === 'user' ? (
                            <UserCircle className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          ) : (
                            <CheckSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{result.title}</div>
                          <div className="flex items-center gap-2 mt-1">
                            {result.type === 'project' ? (
                              <>
                                <span className="text-xs text-muted-foreground">Project</span>
                                {result.subtitle && (
                                  <>
                                    <span className="text-xs text-muted-foreground">•</span>
                                    <span className="text-xs text-muted-foreground">{result.subtitle}</span>
                                  </>
                                )}
                              </>
                            ) : result.type === 'user' ? (
                              <>
                                <span className="text-xs text-muted-foreground">User</span>
                                {result.subtitle && (
                                  <>
                                    <span className="text-xs text-muted-foreground">•</span>
                                    <span className="text-xs text-muted-foreground truncate">{result.subtitle}</span>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                {result.metadata?.tracker && (
                                  <span className="text-xs text-muted-foreground">{result.metadata.tracker}</span>
                                )}
                                {result.metadata?.project && (
                                  <>
                                    <span className="text-xs text-muted-foreground">•</span>
                                    <span className="text-xs text-muted-foreground truncate">{result.metadata.project}</span>
                                  </>
                                )}
                                {result.metadata?.status && (
                                  <>
                                    <span className="text-xs text-muted-foreground">•</span>
                                    <span className="text-xs text-muted-foreground">{result.metadata.status}</span>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    {t('header.noResults', { query: searchQuery })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3">
          {/* User Name - Links to Settings */}
          {user && (
            <Link href="/settings">
              <Button
                variant="ghost"
                className="hidden sm:flex items-center gap-2 hover:bg-muted"
              >
                <User className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {user.firstname && user.lastname 
                    ? `${user.firstname} ${user.lastname}`
                    : user.login || 'User'}
                </span>
              </Button>
            </Link>
          )}

          {/* Language Selector */}
          <LanguageSelector />

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Help Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowHelpSidebar(true)}
            className="hover:bg-muted"
            title={t('common.help')}
          >
            <HelpCircle className="h-5 w-5" />
          </Button>

          {/* Logout Button */}
          {user && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
              title={t('common.signOut')}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </header>

    {/* Help Sidebar */}
    <HelpSidebarContent 
      isOpen={showHelpSidebar} 
      onClose={() => setShowHelpSidebar(false)} 
    />
    </>
  )
}